import React, { useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Button from "@mui/material/Button";
import { Close, PersonRemove, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  BodyMessage,
  ButtonContainer,
  CrossContainer,
  FieldWrapper,
  HeaderDiv,
  HeaderMessage,
  HeaderModal,
  ModalBox,
} from "./styles";
import { DeleteReservationsModalProps } from "./types";
import { useToasts } from "react-toast-notifications";
import theme from "../../theme/theme";
import { deleteUser } from "../../services/user/deleteUser";
import { ROUTES } from "../../routes/constants";
import { useNavigate } from "react-router-dom";
import { Alert, IconButton, TextField } from "@mui/material";
import { postValidatePWD } from "../../services/user/postValidatePWD";
import { getUserById } from "../../services/user/getUser";

const DeleteAccountModal: React.FC<DeleteReservationsModalProps> = ({
  userId,
  disabled = false,
}) => {
  const jwt = localStorage.getItem("jwt");
  const { addToast } = useToasts();

  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);

  const [pwd, setPwd] = React.useState("");
  const [showPwd, setShowPwd] = React.useState(false);
  const [userEmail, setUserEmail] = React.useState("");

  const handleShowPassword = () => setShowPwd(!showPwd);

  const handleOpen = () => {
    setOpen(true);
    setPwd("");
    setShowPwd(false);
  };

  const navigate = useNavigate();

  // ------------------- GET RESOURCES FROM API -------------------

  const getUser = async () => {
    if (userId) {
      await getUserById(userId, jwt!)
        .then((res: any) => {
          if (res && res.data) {
            setUserEmail(res.data.user.email);
          }
        })
        .catch((err: any) => {
          console.log(err, "err");
        });
    }
  };

  const deleteAccount = async () => {
    try {
      // Check if the current password is valid
      const response = await postValidatePWD(jwt!, userEmail, pwd);

      if (response.data.is_correct) {
        await deleteUser(jwt!, userId);
        addToast("Cuenta eliminada correctamente", {
          appearance: "success",
          autoDismiss: true,
        });

        localStorage.removeItem("jwt");
        localStorage.removeItem("user_type");
        navigate(`${ROUTES.LOGIN}`);
      } else {
        addToast("Contraseña incorrecta", {
          appearance: "error",
          autoDismiss: true,
        });
      }
    } catch (err) {
      addToast("Error al eliminar la cuenta", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  // ------------------- USE EFFECTS -------------------
  useEffect(() => {
    getUser();
  }, []);

  return (
    <div>
      <Button
        variant="text"
        onClick={handleOpen}
        startIcon={<PersonRemove />}
        size="large"
        disabled={disabled}
        sx={{
          borderRadius: "12px",
          backgroundColor: theme.color.white,
          padding: "3% 5%",
          color: theme.color.ruby,
          textTransform: "none",
          width: "170px",
          "&:hover": {
            backgroundColor: theme.color.desire,
            color: theme.color.safire,
          },
        }}
      >
        Eliminar cuenta
      </Button>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <ModalBox>
            <HeaderDiv>
              <CrossContainer onClick={handleClose}>
                <IconButton><Close style={{color: theme.color.white}}/></IconButton>
              </CrossContainer>
              <HeaderModal>Eliminar cuenta</HeaderModal>
              <HeaderMessage>
                Tu cuenta y toda tu información serán eliminados
                permanentemente.
              </HeaderMessage>
            </HeaderDiv>
            <BodyMessage>
              Para continuar, por favor ingresa tu contraseña.
            </BodyMessage>
            <FieldWrapper>
              <TextField
                label="Contraseña"
                type={showPwd ? "text" : "password"}
                variant="outlined"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                fullWidth
                required
                InputProps={{
                  style: {
                    borderRadius: "14px",
                  },
                  endAdornment: (
                    <IconButton
                      onClick={handleShowPassword}
                      sx={{
                        minWidth: 0,
                        borderRadius: "30px",
                        background: "transparent",
                      }}
                    >
                      {showPwd ? (
                        <Visibility style={{ color: "A9A9A9" }} />
                      ) : (
                        <VisibilityOff style={{ color: "A9A9A9" }} />
                      )}
                    </IconButton>
                  ),
                }}
              />
            </FieldWrapper>
            <ButtonContainer>
              <Alert
                severity="error"
                sx={{
                  borderRadius: "10px",
                  fontSize: "13px",
                  boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.15)",
                }}
              >
                <strong>¿Estás seguro que deseas eliminar tu cuenta?</strong>{" "}
                Esta acción no puede ser revertida.
              </Alert>
              <Button
                variant="contained"
                startIcon={<PersonRemove />}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  fontSize: "18px",
                  backgroundColor: "#BD2424",
                  color: "#FFFFFF",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#921D1D",
                  },
                }}
                onClick={deleteAccount}
              >
                Eliminar
              </Button>
            </ButtonContainer>
          </ModalBox>
        </Fade>
      </Modal>
    </div>
  );
};

export default DeleteAccountModal;
